import { components } from "@/__openapi__/backend/schema";
import type { AssertTrue, ValidateEnumIsSubtype } from "@/domains/typing/types";
import {
  MDSComboboxFetchDataHandler,
  MDSComboboxItem,
  MDSComboboxProps,
} from "@/mds/components/forms";
import { SessionPartContext } from "@/mds/components/sessions/session-parts/session-part.types";
import { SessionViewMode } from "@/mds/components/sessions/session.constants";
import { MDSSessionErrorKind } from "./session-context";
import { MemCommonMentionKind, MemCommonEditorAssetProxyConfig } from "@mem-labs/common-editor";
import { MDSSessionNotificationInfo } from "@/mds/components/sessions/session-notification/session-notification.types";

/**
 * Session Types
 */

export type SessionEvent = components["schemas"]["SessionEventSummaryResponseSchema"] & {
  feedbackGiven?: "UPVOTE" | "DOWNVOTE" | null;
};
export type AgentActionV1SessionEvent =
  components["schemas"]["SessionEventAgentActionV1EventDataSchema"];
export type AgentPlanningV1SessionEvent =
  components["schemas"]["SessionEventAgentPlanningV1EventDataSchema"];
export type UserActionV1SessionEvent =
  components["schemas"]["SessionEventUserActionV1EventDataSchema"];
export type SystemNotificationV1SessionEvent =
  components["schemas"]["SessionEventSystemNotificationV1EventDataSchema"];
export type AgentDigestV1SessionEvent =
  components["schemas"]["SessionEventAgentDigestV1EventDataSchema"];

export type MarkdownV1SessionPart = components["schemas"]["MarkdownV1SessionPart"];
export type ReasoningV1SessionPart = components["schemas"]["ReasoningV1SessionPart"];
export type WebClipV1SessionPart = components["schemas"]["WebClipV1SessionPart"];
export type ArtifactV1SessionPart = components["schemas"]["ArtifactSessionPartV1"];
export type InterruptV1SessionPart = components["schemas"]["InterruptV1SessionPart"];
export type FactCardV1SessionPart = components["schemas"]["FactCardV1SessionPart"];

export type NoteCreatedV1ArtifactPayload = components["schemas"]["NoteCreatedV1ArtifactPayload"];
export type NoteUpdatedV1ArtifactPayload = components["schemas"]["NoteUpdatedV1ArtifactPayload"];

export type SessionArtifactPayload = NoteCreatedV1ArtifactPayload | NoteUpdatedV1ArtifactPayload;

export type SessionEventData = SessionEvent["data"];
export type SessionEventKindValues = SessionEvent["data"]["kind"];

// The includes all of our older/deprecated parts.
export type UnknownSessionPart =
  | AgentActionV1SessionEvent["value"]["parts"][number]
  | AgentPlanningV1SessionEvent["value"]["parts"][number]
  | UserActionV1SessionEvent["value"]["parts"][number]
  | SystemNotificationV1SessionEvent["value"]["parts"][number];

export enum ClientStatusSessionPartState {
  Placeholder = "Placeholder",
  LoadingIndicator = "LoadingIndicator",
  ErrorMessage = "ErrorMessage",
}

export type AnyStatusV1SessionPart<TState extends ClientStatusSessionPartState, TData = unknown> = {
  kind: SessionPartKind._ClientStatus;
  id: string;
  state: TState;
  data: TData;
};

export type ClientStatusSessionPlaceholderPart =
  AnyStatusV1SessionPart<ClientStatusSessionPartState.Placeholder>;

export type ClientStatusSessionProcessingPart =
  AnyStatusV1SessionPart<ClientStatusSessionPartState.LoadingIndicator>;

export type ClientStatusSessionErrorPart = AnyStatusV1SessionPart<
  ClientStatusSessionPartState.ErrorMessage,
  {
    message: string;
    canRetry: boolean;
    kind: MDSSessionErrorKind;
  }
>;

export type ClientStatusSessionPart =
  | ClientStatusSessionPlaceholderPart
  | ClientStatusSessionProcessingPart
  | ClientStatusSessionErrorPart;

export type AnySessionPart =
  | MarkdownV1SessionPart
  | ReasoningV1SessionPart
  | WebClipV1SessionPart
  | ArtifactV1SessionPart
  | InterruptV1SessionPart
  | FactCardV1SessionPart
  | ClientStatusSessionPart
  | UnknownSessionPart;

export enum SessionEventKind {
  AgentActionV1 = "AGENT_ACTION_V1",
  AgentPlanningV1 = "AGENT_PLANNING_V1",
  UserActionV1 = "USER_ACTION_V1",
  SystemNotificationV1 = "SYSTEM_NOTIFICATION_V1",
  AgentDigestV1 = "AGENT_DIGEST_V1",
}

export enum SessionGroupKind {
  Agent = "AGENT",
  User = "USER",
  System = "SYSTEM",
}

export const GROUPED_SESSION_EVENT_KINDS: Record<SessionGroupKind, SessionEventKind[]> = {
  [SessionGroupKind.Agent]: [SessionEventKind.AgentActionV1, SessionEventKind.AgentPlanningV1],
  [SessionGroupKind.User]: [SessionEventKind.UserActionV1],
  [SessionGroupKind.System]: [SessionEventKind.SystemNotificationV1],
};

export const RENDERABLE_SESSION_EVENT_KINDS: SessionEventKind[] = [
  SessionEventKind.AgentActionV1,
  SessionEventKind.AgentPlanningV1,
  SessionEventKind.UserActionV1,
  SessionEventKind.SystemNotificationV1,
];

type _ValidateSessionEventKindValues = AssertTrue<
  ValidateEnumIsSubtype<SessionEventKind, SessionEventKindValues>
>;

export type SessionPartKindValues = NonNullable<AnySessionPart["kind"]>;

export enum SessionPartKind {
  MarkdownV1 = "MARKDOWN_V1",
  ReasoningV1 = "REASONING_V1",
  WebClipV1 = "WEB_CLIP_V1",
  ArtifactV1 = "ARTIFACT_V1",
  InterruptV1 = "INTERRUPT_V1",
  FactCardV1 = "FACT_CARD_V1",
  // Used internally in the session client
  _ClientStatus = "_CLIENT_STATUS_V1",
}

type _ValidateSessionPartKindValues = AssertTrue<
  ValidateEnumIsSubtype<SessionPartKind, SessionPartKindValues>
>;

export type ArtifactV1SessionPartKindValues = ArtifactV1SessionPart["artifact_kind"];

export enum ArtifactV1SessionPartKind {
  NoteCreatedV1 = "NOTE_CREATED_V1",
  NoteUpdatedV1 = "NOTE_UPDATED_V1",
}

type _ValidateArtifactV1SessionPartKindValues = AssertTrue<
  ValidateEnumIsSubtype<ArtifactV1SessionPartKind, ArtifactV1SessionPartKindValues>
>;

export type SessionStatusValues = components["schemas"]["SessionStatus"];

export type SessionData = components["schemas"]["SessionResponseSchema"];

export enum SessionStatus {
  Initial = "INITIAL",
  Waiting = "WAITING",
  Processing = "PROCESSING",
  Finished = "FINISHED",
  Error = "ERROR",
  Dismissed = "DISMISSED",
  Abandoned = "ABANDONED",
  ContextLimitExceeded = "CONTEXT_LIMIT_EXCEEDED",
  Unknown = "UNKNOWN",
}

type _ValidateSessionStatusValues = AssertTrue<
  ValidateEnumIsSubtype<SessionStatus, SessionStatusValues>
>;

export type MDSComboboxFetchDataHandlerSessionInputFormContext = {
  mentions?: SessionContextMention[];
  implicit_mentions?: SessionContextMention[];
};

export type MDSSessionInputFormSubmitHandler = (payload: {
  content: string;
  context: MDSComboboxFetchDataHandlerSessionInputFormContext;
}) => Promise<boolean>;

type NewSessionHandler = () => void;

export type MDSSessionViewModeChangedHandler = (data: { sessionViewMode: SessionViewMode }) => void;
export type MDSSessionCountdownCompletedHandler = () => void;
export type MDSSessionErrorHandler = (error: {
  message: string;
  type: string;
  canRetry: boolean;
}) => void;
export type MDSSessionRetryHandler = () => void;

export enum SessionFeature {
  // Dynamic View support preview, notification, standard view.
  // W/o it, the view is always "static"
  // (yes, Im horrible at naming)
  DynamicView = "dynamic-view",
  ContextLimitExceededNewChatButton = "context-limit-exceeded-new-chat-button",
  AllowCopy = "allow-copy",
}

/**
 * Fact Source Types - used for resolving fact card sources
 */
export type FactSourceKind = "NOTE" | "COLLECTION";

export type ResolvedFactSource = {
  id: string;
  label: string;
  kind: FactSourceKind;
};

export type ResolveFactSourceHandler = (
  kind: FactSourceKind,
  id: string
) => Promise<ResolvedFactSource | null>;

export type MDSSessionConfig = {
  /**
   * Whether to open links in a new tab
   */
  openLinksInNewTab: boolean;

  /**
   * Whether to prohibit native cmd click enforce Mem-it tab behavior
   */
  prohibitNativeCmdClick?: boolean;

  /**
   * Array of enabled session features.
   * If not specified, all available features are enabled by default.
   * If specified as an empty array, all features are disabled.
   */
  features?: SessionFeature[];

  /**
   * Custom link handler for session links.
   */
  linkHandler?: (
    kind: MemCommonMentionKind,
    id: string,
    target: HTMLElement | null,
    event: React.MouseEvent<Element>
  ) => void;
  onMemItemOver?: (kind: MemCommonMentionKind, id: string, target: HTMLElement | null) => void;
  onMemItemOut?: () => void;
  /**
   * Optional minimum hold time for processing-state reasoning header updates.
   * Regular sessions use the default quick transition; surfaces with very terse
   * reasoning streams can opt into a longer hold.
   */
  reasoningHeaderProcessingMinDisplayMs?: number;
  /**
   * Custom feedback handler for session events.
   */
  feedbackHandler?: (eventId: string, responseKind: "UPVOTE" | "DOWNVOTE") => void;

  /**
   * Optional handler to resolve fact source labels.
   * If not provided, Fact Cards will not be rendered.
   */
  resolveFactSource?: ResolveFactSourceHandler;

  /**
   * Optional async resolver for the asset proxy configuration.
   * When provided, enables images in readonly note previews (e.g. chat artifacts)
   * to load through the authenticated asset proxy.
   */
  resolveAssetProxy?: () => Promise<MemCommonEditorAssetProxyConfig | undefined>;

  /**
   * Generic link click handler for session content.
   * Called for every link click in markdown; return `true` if handled
   * (prevents default navigation). Used by desktop web to intercept
   * attachment links and open them in lightbox viewers.
   */
  onClickLink?: (href: string, target: HTMLElement, event: React.MouseEvent<Element>) => boolean;
};

export type MDSSessionHandlers = {
  handleViewModeChanged: MDSSessionViewModeChangedHandler;
  handleCountdownCompleted: MDSSessionCountdownCompletedHandler;
  handleFetchComboboxData: MDSComboboxFetchDataHandler<MDSComboboxItem>;
  handleInputFormSubmit: MDSSessionInputFormSubmitHandler;
  handleNewSession?: NewSessionHandler;
  handleRetry?: MDSSessionRetryHandler;
};

export type IMDSSessionProps = {
  /**
   * The session title
   */
  sessionTitle: string;
  /**
   * For example, URL of clipped page
   */
  sessionSubtitle: string;
  /**
   * Array of session events to display
   */
  initialSessionEvents: SessionEvent[];
  /**
   * Current status of the session
   */
  initialSessionStatus: SessionStatus;
  /**
   * Whether the session starts collapsed
   */
  initiallyCollapsed?: boolean;
  /**
   * Custom icon to display in the session header
   */
  iconSrc?: string;
  /**
   * Height of the container
   */
  containerHeight: number;
  /**
   * Config
   */
  config: MDSSessionConfig;

  /**
   * Session Handlers
   */
  handlers: MDSSessionHandlers;

  /**
   * Combobox props
   */
  comboboxProps?: Partial<MDSComboboxProps<MDSComboboxItem>>;

  /**
   * Whether the MemIt panel is open.
   * Controls certain UI fades/animations in session content.
   */
  isOpen?: boolean;
};

export interface SessionEventProps {
  event: SessionEvent;
}

export interface IMDSGroupedMessage {
  type: "single" | "reasoning_group";
  event?: SessionEvent;
  events?: SessionEvent[];
}

export interface SessionPartWithContext {
  part: AnySessionPart;
  context: SessionPartContext;
}

export interface IMDSSessionRef {
  upsertEvents: (sessionEvents: SessionEvent[]) => void;
  updateStatus: (sessionStatus: SessionStatus) => void;
  minimizeSession: () => void;
  expandSession: () => void;
  setPreviewMode: () => void;
  setStandardMode: () => void;
  startCountdown: (durationMs: number) => void;
  setSessionError: (error: {
    message: string;
    type: MDSSessionErrorKind;
    canRetry: boolean;
  }) => void;
  clearSessionError: () => void;
  publishNotification: (args: { notificationInfo: MDSSessionNotificationInfo }) => void;
  focusComboboxInput: () => void;
  setCurrentSessionId: (sessionId: string | null) => void;
}

export enum SessionContextKind {
  UserContextV1 = "USER_CONTEXT_V1",
}

export enum SessionContextMentionKind {
  Note = "NOTE",
  Collection = "COLLECTION",
  SearchNotesByTimeRange = "SEARCH_NOTES_BY_TIME_RANGE",
}

export type SessionContextMention =
  | components["schemas"]["NoteSessionMention"]
  | components["schemas"]["CollectionSessionMention"]
  | components["schemas"]["SearchNotesByTimeRangeSessionMention"];

export type NoteSessionMention = components["schemas"]["NoteSessionMention"];
export type CollectionSessionMention = components["schemas"]["CollectionSessionMention"];
